import { type Dispatch, type SetStateAction, useEffect } from "react";

/**
 * ⌘K / Ctrl+K toggles the command palette from anywhere in the shell, inputs included: a
 * palette conventionally opens from inside a field. ⌘B is the sidebar's (packages/ui), and
 * Alt is excluded so a layout that types a character with Alt+K keeps it.
 */
export function useCommandPaletteHotkey(setOpen: Dispatch<SetStateAction<boolean>>) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isCommandPaletteHotkey(event)) return;
      event.preventDefault();
      // A held key repeats keydown; toggling on each would flicker and could end closed.
      if (event.repeat) return;
      setOpen((open) => !open);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setOpen]);
}

/**
 * Whether a keydown is ⌘K / Ctrl+K. Chrome sends a keydown with no `key` when the user picks
 * an autofill suggestion, so `key` is checked before it is read, whatever its type says.
 */
export function isCommandPaletteHotkey(
  event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">,
): boolean {
  if (typeof event.key !== "string" || event.key.toLowerCase() !== "k") return false;
  return (event.metaKey || event.ctrlKey) && !event.altKey;
}

/** The shortcut as the sidebar shows it: this platform's modifier, then K. */
export function commandPaletteShortcutLabel(): string {
  const platform = typeof navigator === "undefined" ? "" : navigator.platform;
  return /Mac|iPhone|iPad|iPod/.test(platform) ? "⌘K" : "Ctrl K";
}
